import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Building2, CalendarPlus, Camera, ImagePlus, Fingerprint, ShieldCheck } from 'lucide-react';
import { AppHeader } from '../../components/AppHeader';
import { Button } from '../../components/Button';
import { LegalLinks } from '../legal/LegalPage';
import { customerStatusKey } from './customerStatus';
import { SHOW_WALLD_BRANDING } from '../../config/brand';
import { ImagePrepareError, imageUploadErrorKey, MAX_UPLOAD_MEGABYTES, type PhotoSourceReadDiagnostic } from '../../editor/image';
import { telegram } from '../../telegram/bridge';
import { CameraCapture } from './CameraCapture';
import type { Locale } from '../../../shared/i18n';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { useI18n } from '../../i18n/I18nProvider';
import { formatStudioDate } from '../../utils/studioDate';
import { FeatureGate, LockedFeature, useCapability } from '../capabilities/Capability';
import { logPhotoFlow } from '../../diagnostics/photoFlow';
import { deleteLocalPhoto, LocalPhotoStorageError, prepareAndStoreLocalPhoto, type LocalPhotoReference } from '../../storage/photos';

export interface HomeTryon {
  id: string; designName: string; designCategory: string; previewUrl: string; bookingStatus: string; createdAt: number; businessTimezone?: string;
}

interface Props {
  tryons: HomeTryon[];
  historyLoading?: boolean;
  adminPreview?: boolean;
  onReturnAdmin?: () => void;
  onTryonOpen: () => void;
  onPhoto: (photo: LocalPhotoReference) => void | Promise<void>;
  onPhotoError: (error: unknown, file: File) => void;
  onPhotoReadRetrySuccess?: (diagnostic: PhotoSourceReadDiagnostic) => void;
  externalError?: string;
  onBook: (id: string) => void;
  onView: (id: string) => void;
  onForStudios: () => void;
  onPrivacy?: () => void;
  enabledLocales?: Locale[];
  preferredLocale?: Locale | null;
  verified?: boolean;
}

export function HomeScreen({ tryons, historyLoading, adminPreview, onReturnAdmin, onTryonOpen, onPhoto, onPhotoError, onPhotoReadRetrySuccess, externalError, onBook, onView, onForStudios, onPrivacy, enabledLocales, preferredLocale, verified }: Props) {
  const { t, locale } = useI18n();
  const gallery = useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [capabilityNotice, setCapabilityNotice] = useState('');
  const photoOperation = useRef(0);
  const bookingCapability = useCapability('booking.enabled');

  useEffect(() => { if (externalError) setError(externalError); }, [externalError]);
  useEffect(() => () => { photoOperation.current += 1; }, []);

  const choose = async (file?: File) => {
    if (!file) return;
    const operation = ++photoOperation.current;
    const started = performance.now();
    logPhotoFlow('PHOTO_PICK_FILE_RECEIVED', { mime: file.type || 'unknown', bytes: file.size });
    logPhotoFlow('PHOTO_MIME', { mime: file.type || 'unknown' });
    logPhotoFlow('PHOTO_SIZE', { bytes: file.size });
    setError(''); setProcessing(true);
    let stored: LocalPhotoReference | null = null;
    let adopted = false;
    try {
      stored = await prepareAndStoreLocalPhoto(file, {
        maxEdge: 2400,
        quality: .9,
        shouldContinue: () => operation === photoOperation.current,
        onDiagnostic: (diagnostic) => console.debug('[WALLD photo]', { ...diagnostic, platform: telegram.platform() }),
        onSourceReadDiagnostic: (diagnostic) => {
          console.debug('[WALLD photo]', { ...diagnostic, platform: telegram.platform() });
          if (diagnostic.outcome === 'retry_succeeded') onPhotoReadRetrySuccess?.(diagnostic);
        },
      });
      if (operation !== photoOperation.current) { await deleteLocalPhoto(stored.id).catch(() => undefined); return; }
      setCameraOpen(false);
      telegram.haptic('success');
      await onPhoto(stored);
      adopted = true;
      console.debug('[WALLD timing]', { flow: 'photo_to_library', stage: 'photo_ready', durationMs: Math.round(performance.now() - started), outcome: 'ok' });
    } catch (cause) {
      if (stored && !adopted) await deleteLocalPhoto(stored.id).catch(() => undefined);
      if (operation !== photoOperation.current || cause instanceof DOMException && cause.name === 'AbortError') return;
      onPhotoError(cause, file);
      const recoverableDecode = cause instanceof ImagePrepareError && !['image_too_large', 'unsupported_image', 'photo_read_failed'].includes(cause.code);
      setError(cause instanceof LocalPhotoStorageError ? t('home.localPhotoError')
        : recoverableDecode ? t('home.imageOpenError') : t(imageUploadErrorKey(cause), { size: MAX_UPLOAD_MEGABYTES }));
      telegram.haptic('error');
      console.debug('[WALLD timing]', { flow: 'photo_to_library', stage: 'photo_prepare', durationMs: Math.round(performance.now() - started), outcome: 'failed' });
    } finally { if (operation === photoOperation.current) setProcessing(false); }
  };

  return (
    <main className="screen home-screen">
      <AppHeader title={t('home.title')} action={<LanguageSwitcher enabledLocales={enabledLocales} preferredLocale={preferredLocale} verified={verified} />} />
      {adminPreview && <div className="customer-preview-banner"><span>{t('home.adminPreview')}</span><Button variant="secondary" onClick={onReturnAdmin}>{t('home.returnAdmin')}</Button></div>}
      <div className="screen-content home-screen__content">
        {tryons.length > 0 && <p className="eyebrow home-welcome-back">{t('home.welcomeBack')}</p>}
        <FeatureGate feature="editor.basic" locked={<section className={`hero-action-card${tryons.length ? ' hero-action-card--compact' : ''}`}><LockedFeature title={t(tryons.length ? 'home.startNew' : 'home.tryPhoto')} compact /></section>}><section className={`hero-action-card${tryons.length ? ' hero-action-card--compact' : ''}`}>
          <div className="hero-action-card__icon" aria-hidden="true"><ImagePlus /></div>
          <p className="eyebrow">{t('home.privatePreview')}</p>
          <h2>{t(tryons.length ? 'home.startNew' : 'home.tryPhoto')}</h2>
          <p className="lead">{t('home.lead')}</p>
          <div className="home-photo-actions"><Button block busy={processing} icon={<ImagePlus />} onClick={() => { onTryonOpen(); setError(''); logPhotoFlow('PHOTO_PICK_START', { source: 'gallery' }); gallery.current?.click(); }}>{t(processing ? 'home.openingPhoto' : error ? 'home.chooseAnotherPhoto' : 'home.upload')}</Button><Button block variant="secondary" icon={<Camera />} onClick={() => { onTryonOpen(); setError(''); logPhotoFlow('PHOTO_PICK_START', { source: 'camera' }); setCameraOpen(true); }} disabled={processing}>{t(error ? 'home.takePhoto' : 'home.camera')}</Button></div>
<div className="privacy-row"><ShieldCheck aria-hidden="true" /><span><strong>{t('home.processed')}</strong>{t('home.privacy')}</span></div>
          <input ref={gallery} className="visually-hidden" type="file" accept="image/*,.heic,.heif" onChange={(event) => { const input = event.currentTarget; const selected = input.files?.[0]; input.value = ''; void choose(selected); }} />
          {error && <p className="inline-error" role="alert">{error}</p>}
        </section></FeatureGate>
        {historyLoading && <section className="history-skeleton" aria-label={t('home.loadingHistory')}><span /><span /></section>}
        {tryons.length > 0 && <section className="history-section">
          <div className="section-heading history-heading"><div><h1>{t('home.myTryons')}</h1></div></div>
          <div className="tryon-grid">{tryons.map((item) => <article className="tryon-card" key={item.id}>
            <button className={`tryon-card__preview${item.previewUrl ? ' tryon-card__preview--image' : ''}`} onClick={() => onView(item.id)} aria-label={`${t('common.view')} ${item.designName}`}>{item.previewUrl ? <img src={item.previewUrl} alt={`${item.designName} Try-On`} /> : <span>{t('home.previewUnavailable')}</span>}</button>
            <div className="tryon-card__copy"><span><strong>{item.designName}</strong><small>{formatStudioDate(item.createdAt, locale, item.businessTimezone || 'UTC')} · {item.designCategory || t('common.custom')}</small></span><span className={`status-pill status-pill--${item.bookingStatus}`}>{t(customerStatusKey(item.bookingStatus))}</span></div>
            <div className="tryon-card__primary">{bookingCapability.isEnabled ? <Button icon={<CalendarPlus />} onClick={() => onBook(item.id)}>{t('common.book')}</Button> : bookingCapability.isLocked ? <Button variant="secondary" icon={<CalendarPlus />} aria-disabled="true" onClick={() => setCapabilityNotice(t('capability.otherPlan'))}>{t('common.book')} ★</Button> : null}<Button variant="secondary" onClick={() => onView(item.id)}>{t('common.view')}</Button></div>
          </article>)}</div>
          {capabilityNotice && <p className="capability-inline-note" role="status">{capabilityNotice}</p>}
        </section>}
        {verified && onPrivacy && <button className="privacy-link" type="button" onClick={onPrivacy}><Fingerprint /><span>{t('privacy.title')}</span><ArrowRight /></button>}
        <button className="studios-link" onClick={onForStudios}><Building2 /><span><strong>{t('home.forStudios')}</strong><small>{t('home.forStudiosSub')}</small></span><ArrowRight /></button>
        {SHOW_WALLD_BRANDING && <footer className="walld-credit">{t('brand.builtBy')} · <a href="https://t.me/walldstudio" onClick={(event) => { event.preventDefault(); telegram.openTelegramLink('https://t.me/walldstudio'); }}>@walldstudio</a></footer>}
        <LegalLinks />
      </div>
      {cameraOpen && <CameraCapture onCapture={(file) => void choose(file)} onClose={() => setCameraOpen(false)} />}
    </main>
  );
}
